import React, { useState, useEffect, Fragment, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  EllipsisHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  PlusIcon,
} from '@heroicons/react/20/solid';
import { Cog8ToothIcon } from '@heroicons/react/24/outline';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PageHeader, TWButton, TWModal } from '../../../components/ui';
import storeApi from '../../../services/storeApi';
import terminalApi from '../../../services/terminalApi';
import { Menu, Transition } from '@headlessui/react';
import ModalDelete from '../../../components/modal-delete/modal-delete';
import StoreSlugUpsertModal from '../store-settings/components/store-slug-upsert-modal';
import NoStoreLogo from '../../../assets/no-store-logo.svg';
import kitchenterminalApi from '../../../services/kitchenterminalApi';
import KioskLogo from '../../../assets/kiosk-logo.png';
import KitchenTerminalLogo from '../../../assets/kitchen-terminal-logo.png';
import Notification from '../../../components/notification/notification';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { QRCodeCanvas } from 'qrcode.react';
import StoreDetailSkeleton from './components/store-detail-skeleton';
import './store-detail.css';

const utc = require('dayjs/plugin/utc');
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.Ls.en.weekStart = 1;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const fieldClass =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:disabled:bg-slate-800';

const StoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [storeDetail, setStoreDetail] = useState({});
  const [terminalList, setTerminalList] = useState([]);
  const [kitchenterminalList, setKitchenTerminalList] = useState([]);

  const [isKitchenTerminal, setIsKitchenTerminal] = useState(false);

  const [form, setForm] = useState({
    id: '',
    code: '',
    name: '',
    serialnumber: '',
    storeid: id,
  });
  const [loading, setLoading] = useState({
    initialLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    loginAsCompany: false,
  });

  const [errors, setErrors] = useState({
    code: false,
    name: false,
    slug: false,
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const [dialogs, setDialogs] = useState({
    upsert: false,
    delete: false,
    slugUpsert: false,
  });

  const getStoreDetail = useCallback(async () => {
    try {
      const res = await storeApi.getById(id);
      if (res.data) {
        setStoreDetail(res.data.data);
      }
    } catch (error) {
      // optional: surface toast
    }
  }, [id]);

  const getTerminalList = useCallback(async () => {
    try {
      const query = `?storeid=${id}&isdeleted=false`;
      const res = await terminalApi.list(query);
      if (res.data) {
        setTerminalList(res.data.data);
      }
    } catch (error) {
      // optional: surface toast
    }
  }, [id]);

  const getKitchenTerminalList = useCallback(async () => {
    try {
      const res = await kitchenterminalApi.list(id);
      if (res.data) {
        setKitchenTerminalList(res.data.data);
      }
    } catch (error) {
      // optional: surface toast
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading((prev) => ({ ...prev, initialLoading: true }));
      try {
        await Promise.all([
          getStoreDetail(),
          getTerminalList(),
          getKitchenTerminalList(),
        ]);
      } finally {
        if (!cancelled) {
          setLoading((prev) => ({ ...prev, initialLoading: false }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getStoreDetail, getTerminalList, getKitchenTerminalList]);

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onFormSubmit = async () => {
    if (!form.code || !form.name) {
      setErrors((prev) => ({
        ...prev,
        code: !form.code ? true : false,
        name: !form.name ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, code: false, name: false }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = {
        ...form,
        serialnumber: isKitchenTerminal ? '' : form.serialnumber,
      };
      const res = isKitchenTerminal
        ? await kitchenterminalApi.upsert(body)
        : await terminalApi.upsert(body);
      if (res.data.data) {
        setDialogs((prev) => ({ ...prev, upsert: false }));
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      if (isKitchenTerminal) await getKitchenTerminalList();
      else await getTerminalList();
    }
  };

  const onClickAddTerminal = (isKitchen) => {
    setIsKitchenTerminal(isKitchen);
    setForm({ id: '', code: '', serialnumber: '', name: '', storeid: id });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onClickEdit = (val, isKitchen) => {
    setIsKitchenTerminal(isKitchen);
    setForm({
      id: val.id,
      code: val.code,
      name: val.name,
      serialnumber: val.serialnumber,
      storeid: val.storeid,
    });

    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({ ...prev, code: false, name: false }));
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickOpenStoreSlugModal = () => {
    setDialogs((prev) => ({ ...prev, slugUpsert: true }));
  };

  const onCloseStoreSlugModal = () => {
    setErrors((prev) => ({ ...prev, slug: false }));

    setDialogs((prev) => ({ ...prev, slugUpsert: false }));
  };

  const onSubmitStoreSlugModal = async (slug) => {
    if (slug === '' || !slug) {
      setErrors((prev) => ({ ...prev, slug: true }));
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));

      const body = {
        ...storeDetail,
        slug,
        websiteclosedat: storeDetail.websiteclosedat || '',
      };
      const formData = new FormData();

      for (const key of Object.keys(body)) {
        formData.append(key, body[key]);
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };

      const res = await storeApi.upsert(formData, config);

      if (res.data) {
        await getStoreDetail();
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({
        ...prev,
        upsertLoading: false,
      }));
      onCloseStoreSlugModal();
    }
  };

  const onClickDeleteKiosk = () => {
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      if (isKitchenTerminal) {
        await kitchenterminalApi.deleteById(form.id);
        getKitchenTerminalList();
      } else {
        await terminalApi.deleteById(form.id);
        getTerminalList();
      }
    } catch (error) {
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById('qrCodeEl');
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    let downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${storeDetail.slug}-QRCode.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const checkConnection = (date) => dayjs().diff(dayjs(date), 'minutes') <= 7;

  return (
    <div className="store-detail-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      {loading.initialLoading ? (
        <StoreDetailSkeleton />
      ) : (
        <div className="mx-auto max-w-5xl space-y-12">
          <header className="reveal flex items-start gap-4">
            <img
              className="h-14 w-14 shrink-0 rounded-md object-cover ring-2 ring-white shadow-sm dark:ring-slate-700"
              src={storeDetail.storelogo || NoStoreLogo}
              alt=""
            />
            <PageHeader
              title={storeDetail.name}
              description={
                <span className="inline-flex items-start gap-2">
                  <BuildingOfficeIcon
                    className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                    aria-hidden="true"
                  />
                  <span className="capitalize">{storeDetail.address}</span>
                </span>
              }
              className="!flex-1"
              actions={
                <>
                  {storeDetail.slug ? (
                    <Link
                      to={`${process.env.REACT_APP_ORDER_URL}/${storeDetail.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-md bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-200/90 dark:bg-emerald-900/40 dark:text-emerald-200 dark:hover:bg-emerald-900/60"
                    >
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      {process.env.REACT_APP_ORDER_URL.replace('https://', '')}/
                      {storeDetail.slug}
                      <ArrowTopRightOnSquareIcon
                        className="h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300"
                        aria-hidden="true"
                      />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onClickOpenStoreSlugModal()}
                      className="inline-flex items-center justify-center rounded-md bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900 shadow-sm transition hover:bg-amber-200/90 dark:bg-amber-900/35 dark:text-amber-100 dark:hover:bg-amber-900/50"
                    >
                      Create order link
                    </button>
                  )}
                  <TWButton
                    icon={Cog8ToothIcon}
                    label="Store settings"
                    onClick={() => navigate('settings')}
                  />
                </>
              }
            />
          </header>

          <section className="min-h-[120px] space-y-6">
            <div className="reveal flex flex-col gap-4 border-b border-slate-200 pb-6 dark:border-slate-700 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Kiosks
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Create new and manage all your kiosks
                </p>
              </div>
              <button
                type="button"
                onClick={() => onClickAddTerminal(false)}
                className="inline-flex h-10 shrink-0 items-center gap-2 self-start rounded-md bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-95 dark:bg-sky-500 dark:hover:bg-sky-400 sm:self-center"
              >
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
                Add
              </button>
            </div>
            <div className="mt-2 flow-root">
              <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8">
                {terminalList.map((terminal, index) => (
                  <li
                    key={index}
                    className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
                    style={{ animationDelay: `${index * 40}ms` }}
                  >
                    <div className="flex items-center gap-x-4 border-b border-slate-100 bg-slate-50/90 p-5 dark:border-slate-600 dark:bg-slate-700/80">
                      <img
                        src={KioskLogo}
                        alt={terminal.name}
                        className="h-12 w-12 flex-none rounded-md bg-white object-cover ring-1 ring-slate-900/10 dark:ring-slate-600"
                      />
                      <div className="text-sm font-medium leading-6 text-gray-900 dark:text-slate-200">
                        {terminal.name}
                      </div>
                      <Menu as="div" className="relative ml-auto">
                        <Menu.Button className="-m-2.5 block p-2.5 text-gray-400 hover:text-gray-500">
                          <span className="sr-only">Open options</span>
                          <EllipsisHorizontalIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 z-10 mt-0.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none dark:bg-slate-800 dark:ring-slate-700">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => onClickEdit(terminal, false)}
                                  className={classNames(
                                    active
                                      ? 'bg-gray-50 dark:bg-slate-700'
                                      : 'text-gray-900 dark:text-slate-100',
                                    'block w-full px-3 py-1 text-left text-sm leading-6',
                                  )}
                                >
                                  Edit
                                  <span className="sr-only">
                                    , {terminal.name}
                                  </span>
                                </button>
                              )}
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                    <dl className="-my-3 divide-y divide-slate-100 px-5 py-4 text-sm leading-6 dark:divide-slate-600">
                      <div className="flex justify-between gap-x-4 py-3">
                        <dt className="text-slate-500 dark:text-slate-400">
                          Device Status
                        </dt>
                        <dd className="text-gray-700 dark:text-slate-200">
                          {terminal.lastup ? (
                            checkConnection(terminal.lastup) ? (
                              <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                                <svg
                                  className="h-1.5 w-1.5 fill-green-500"
                                  viewBox="0 0 6 6"
                                  aria-hidden="true"
                                >
                                  <circle cx={3} cy={3} r={3} />
                                </svg>
                                Connected
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-x-1.5 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                                <svg
                                  className="h-1.5 w-1.5 fill-red-500"
                                  viewBox="0 0 6 6"
                                  aria-hidden="true"
                                >
                                  <circle cx={3} cy={3} r={3} />
                                </svg>
                                Not Connected
                              </span>
                            )
                          ) : (
                            '-'
                          )}
                        </dd>
                      </div>

                      <div className="flex justify-between gap-x-4 py-3">
                        <dt className="text-gray-500 dark:text-slate-400">
                          Reader Status
                        </dt>
                        <dd className="text-gray-700 dark:text-slate-200">
                          {terminal.lastconnectionupdate ? (
                            checkConnection(terminal.lastconnectionupdate) ? (
                              <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                                <svg
                                  className="h-1.5 w-1.5 fill-green-500"
                                  viewBox="0 0 6 6"
                                  aria-hidden="true"
                                >
                                  <circle cx={3} cy={3} r={3} />
                                </svg>
                                Ready
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-x-1.5 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                                <svg
                                  className="h-1.5 w-1.5 fill-red-500"
                                  viewBox="0 0 6 6"
                                  aria-hidden="true"
                                >
                                  <circle cx={3} cy={3} r={3} />
                                </svg>
                                Not Ready
                              </span>
                            )
                          ) : (
                            '-'
                          )}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-x-4 py-3">
                        <dt className="text-gray-500 dark:text-slate-400">
                          Payment SN
                        </dt>
                        <dd className="flex items-start gap-x-2">
                          <div className="font-medium text-gray-900 dark:text-slate-200">
                            {terminal.serialnumber
                              ? terminal.serialnumber
                              : '-'}
                          </div>
                        </dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="min-h-[120px] space-y-6">
            <div className="reveal flex flex-col gap-4 border-b border-slate-200 pb-6 dark:border-slate-700 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Kitchen terminals
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Create new and manage all your kitchen terminals
                </p>
              </div>
              <button
                type="button"
                onClick={() => onClickAddTerminal(true)}
                className="inline-flex h-10 shrink-0 items-center gap-2 self-start rounded-md bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-95 dark:bg-sky-500 dark:hover:bg-sky-400 sm:self-center"
              >
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
                Add
              </button>
            </div>
            <div className="mt-2 flow-root">
              <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8">
                {kitchenterminalList.map((terminal, index) => (
                  <li
                    key={index}
                    className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
                    style={{ animationDelay: `${index * 40}ms` }}
                  >
                    <div className="flex items-center gap-x-4 border-b border-slate-100 bg-slate-50/90 p-5 dark:border-slate-600 dark:bg-slate-700/80">
                      <img
                        src={KitchenTerminalLogo}
                        alt={terminal.name}
                        className="h-12 w-12 flex-none rounded-md bg-white object-cover ring-1 ring-slate-900/10 dark:ring-slate-600"
                      />
                      <div className="text-sm font-medium leading-6 text-gray-900 dark:text-slate-200">
                        {terminal.name}
                      </div>
                      <Menu as="div" className="relative ml-auto">
                        <Menu.Button className="-m-2.5 block p-2.5 text-gray-400 hover:text-gray-500">
                          <span className="sr-only">Open options</span>
                          <EllipsisHorizontalIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 z-10 mt-0.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none dark:bg-slate-800 dark:ring-slate-700">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => onClickEdit(terminal, true)}
                                  className={classNames(
                                    active
                                      ? 'bg-gray-50 dark:bg-slate-700'
                                      : 'text-gray-900 dark:text-slate-100',
                                    'block w-full px-3 py-1 text-left text-sm leading-6',
                                  )}
                                >
                                  Edit
                                  <span className="sr-only">
                                    , {terminal.name}
                                  </span>
                                </button>
                              )}
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                    <dl className="-my-3 divide-y divide-slate-100 px-5 py-4 text-sm leading-6 dark:divide-slate-600">
                      <div className="flex justify-between gap-x-4 py-3">
                        <dt className="text-slate-500 dark:text-slate-400">
                          Status
                        </dt>
                        <dd className="text-slate-700 dark:text-slate-200">
                          <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                            <svg
                              className="h-1.5 w-1.5 fill-green-500"
                              viewBox="0 0 6 6"
                              aria-hidden="true"
                            >
                              <circle cx={3} cy={3} r={3} />
                            </svg>
                            Active
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="min-h-[120px] space-y-6">
            <div className="reveal border-b border-slate-200 pb-6 dark:border-slate-700">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                QR code
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Scan this QR code to open the store ordering page
              </p>
            </div>
            <div className="flow-root">
              <ul className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <li className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                  <div className="flex flex-col gap-4 border-b border-slate-100 bg-slate-50/90 p-5 sm:flex-row sm:items-center dark:border-slate-600 dark:bg-slate-700/80">
                    <div className="shrink-0 rounded-md bg-white p-2 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-600">
                      <QRCodeCanvas
                        id="qrCodeEl"
                        value={`${process.env.REACT_APP_ORDER_URL}/${storeDetail.slug}`}
                        size={200}
                        level={'H'}
                        includeMargin={true}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-bold leading-snug text-slate-900 dark:text-slate-100">
                        {storeDetail.name}
                      </p>
                      <p className="mt-1 break-all text-xs text-slate-500 dark:text-slate-400">
                        {process.env.REACT_APP_ORDER_URL}/{storeDetail.slug}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={downloadQR}
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-sky-400"
                      aria-label="Download QR code"
                    >
                      <ArrowDownTrayIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </section>
        </div>
      )}
      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title={
          form.id
            ? isKitchenTerminal
              ? 'Edit kitchen terminal'
              : 'Edit kiosk'
            : isKitchenTerminal
              ? 'Add kitchen terminal'
              : 'Add kiosk'
        }
        size="2xl"
        showCloseButton
        dismissible={false}
        bodyClassName="max-h-[calc(90vh-8rem)] space-y-5 overflow-y-auto"
        panelClassName="dark:text-slate-200"
        footer={
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex justify-center sm:justify-start">
              {form.id && (
                <TWButton
                  variant="danger"
                  icon={TrashIcon}
                  disabled={loading.deleteLoading}
                  loading={loading.deleteLoading}
                  onClick={() => onClickDeleteKiosk()}
                />
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <TWButton
                variant="default"
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={() => onCloseUpsert()}
              />
              <TWButton
                variant="primary"
                label={
                  isKitchenTerminal
                    ? form.id
                      ? 'Save kitchen terminal'
                      : 'Add kitchen terminal'
                    : form.id
                      ? 'Save kiosk'
                      : 'Add kiosk'
                }
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={() => onFormSubmit()}
              />
            </div>
          </div>
        }
      >
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-slate-700 dark:text-slate-400"
          >
            {isKitchenTerminal ? 'Kitchen terminal name*' : 'Kiosk name*'}
          </label>
          <div className="mt-1">
            <input
              value={form.name}
              onChange={(e) => onFormChange(e.target.value, 'name')}
              type="text"
              name="name"
              id="name"
              className={fieldClass}
              placeholder={
                isKitchenTerminal
                  ? 'e.g “Kitchen 1, etc.”'
                  : 'e.g “Kiosk 1, etc.”'
              }
            />
            <div className="h-2 mt-1  text-xs">
              {errors.name && (
                <p className="text-red-700">Name can not be empty</p>
              )}
            </div>
          </div>
        </div>
        <div>
          <label
            htmlFor="verificationcode"
            className="block text-sm font-medium text-slate-700 dark:text-slate-400"
          >
            Verification code*
          </label>
          <div className="mt-1">
            <input
              value={form.code}
              onChange={(e) => onFormChange(e.target.value, 'code')}
              type="text"
              name="code"
              id="code"
              className={fieldClass}
            />
            <div className="h-2 mt-1  text-xs">
              {errors.code && (
                <p className="text-red-700">Code can not be empty</p>
              )}
            </div>
          </div>
        </div>
        {!isKitchenTerminal && (
          <div>
            <label
              htmlFor="serialnumber"
              className="block text-sm font-medium text-slate-700 dark:text-slate-400"
            >
              Serial number
            </label>
            <div className="mt-1">
              <input
                value={form.serialnumber}
                onChange={(e) => onFormChange(e.target.value, 'serialnumber')}
                type="text"
                name="serialnumber"
                id="serialnumber"
                className={fieldClass}
              />
            </div>
          </div>
        )}
      </TWModal>
      <StoreSlugUpsertModal
        onSubmit={onSubmitStoreSlugModal}
        show={dialogs.slugUpsert}
        loading={loading.upsertLoading}
        storeDetail={storeDetail}
        setStoreDetail={setStoreDetail}
        onClose={onCloseStoreSlugModal}
        errors={errors}
      />
      <ModalDelete
        title={
          isKitchenTerminal ? 'Delete Kitchen Terminal' : 'Delete Terminal'
        }
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={form}
      />
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default StoreDetail;
